import { useQuery } from '@tanstack/react-query'

import {
  approvalMakerQueryOptions,
  approvalInboxQueryOptions,
  approvalTrailQueryOptions,
  myRequestsQueryOptions,
} from '@/api/approvals-api'
import { myBranchesQueryOptions } from '@/api/reference-api'
import type { Branch } from '@/api/types'

/** Reads behind the approvals screens. */

export const useApprovalInbox = () => useQuery(approvalInboxQueryOptions())

/** The caller's own submissions — see {@link myRequestsQueryOptions}. */
export const useMyRequests = () => useQuery(myRequestsQueryOptions())

/** The trail is only ever read when a panel showing it is open — see the query's own note. */
export const useApprovalTrail = (requestId: string, page: number, enabled: boolean) =>
  useQuery({ ...approvalTrailQueryOptions({ requestId, page }), enabled })

export const useApprovalMaker = (username: string, enabled: boolean) =>
  useQuery(approvalMakerQueryOptions(username, enabled))

export interface CheckerReach {
  /** The branches this user is explicitly granted. Their home branch plus any access grants. */
  branches: Branch[]
  /**
   * True for head-office and audit roles.
   *
   * It changes what the branch list *means*. `ApprovalService` applies a branch predicate to the
   * inbox only when this is false; for a cross-branch checker the queue has no branch filter at
   * all, and the granted list is a floor rather than the full set. So a screen must never present
   * the branch list as the extent of what such a checker can be shown — the queue will contain
   * branches that are not in it.
   */
  canAccessAllBranches: boolean
  /**
   * False while no module owns branches as records, which is why every code and name is null. The
   * honest answer then is the identifier, and this flag is how the server says so.
   */
  directoryAvailable: boolean
  isPending: boolean
}

/** Everything a screen needs to say which branch a request belongs to, and whether it may act. */
export function useCheckerReach(): CheckerReach {
  const query = useQuery(myBranchesQueryOptions())

  return {
    branches: query.data?.items ?? [],
    canAccessAllBranches: query.data?.canAccessAllBranches ?? false,
    directoryAvailable: query.data?.directoryAvailable ?? false,
    isPending: query.isPending,
  }
}
